"use client";

import { useState, useTransition } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  submitContactInfo,
  type SubmitContactInfoState,
} from "#/actions/job_application";

const initialState: SubmitContactInfoState = {
  message: "",
  applicationId: "",
};

export default function ApplicationForm({ jobPostId }: { jobPostId: string }) {
  const submitContactInfoFn = useServerFn(submitContactInfo);
  const [state, setState] = useState(initialState);
  const [isPending, startTransition] = useTransition();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const getString = (value: FormDataEntryValue | null) =>
      typeof value === "string" ? value : "";

    startTransition(async () => {
      const result = await submitContactInfoFn({
        data: {
          email: getString(formData.get("email")),
          firstName: getString(formData.get("firstName")),
          jobPostId,
          lastName: getString(formData.get("lastName")),
          phone: getString(formData.get("phone")),
        },
      });

      setState(result);
    });
  };

  if (state.applicationId.length === 0) {
    return (
      <form
        className="my-5 w-full"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="text-xl font-medium leading-6 text-gray-900">
          Apply for this position
        </h3>
        <div className="mt-2 px-7 py-3" role="document">
          <p className="text-sm text-gray-500">{state.message}</p>
        </div>

        <div className="mb-6">
          <label
            htmlFor="firstName"
            className="mb-2 block text-sm font-medium text-gray-900"
          >
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            required
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
            placeholder="first name here"
          />
        </div>
        <div className="mb-6">
          <label
            htmlFor="lastName"
            className="mb-2 block text-sm font-medium text-gray-900"
          >
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            required
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
            placeholder="last name here"
          />
        </div>
        <div className="mb-6">
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-medium text-gray-900"
          >
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
            placeholder="email here"
          />
        </div>
        <div className="mb-6">
          <label
            htmlFor="phone"
            className="mb-2 block text-sm font-medium text-gray-900"
          >
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
            placeholder="phone number here"
          />
        </div>
        <button
          type="submit"
          disabled={isPending}
          className="w-full rounded-lg bg-blue-500 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-300"
        >
          {isPending ? "Submitting..." : "Next"}
        </button>
      </form>
    );
  } else {
    return (
      <div className="my-5 w-full">
        <h3 className="text-center text-xl font-medium leading-6 text-green-700">
          Application Submitted
        </h3>
        <div className="mt-2 px-7 py-3" role="document">
          <p className="text-center text-sm text-gray-500">
            You'll receive an email confirmation shortly.
          </p>
        </div>
      </div>
    );
  }
}
